import { useCallback, useEffect, useState } from 'react';
import { getConvexAuthToken, useAppAuth } from '@/lib/appAuth';
import { claimShelfPurchases, getPendingShelfPurchases } from '@/services/shelfClaims';

// Paid fulfilment stays available even when creating new guest checkouts is
// disabled. Convex binds every claim to the signed-in, verified email.
export type ShelfClaimStatus = 'idle' | 'checking' | 'adding' | 'added' | 'none' | 'unavailable' | 'verify_email';
export interface ShelfClaimState {
  status: ShelfClaimStatus;
  addedCount: number;
  products: string[];
  retry?: () => void;
}
const INERT: ShelfClaimState = { status: 'idle', addedCount: 0, products: [] };

export function useShelfClaim(): ShelfClaimState {
  const { isLoaded, isSignedIn, getToken, userId } = useAppAuth();
  const [state, setState] = useState<ShelfClaimState>(INERT);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(count => count + 1), []);
  useEffect(() => {
    if (!isLoaded || !isSignedIn) { setState(INERT); return; }
    let cancelled = false;
    const settle = (next: ShelfClaimState) => { if (!cancelled) setState(next); };
    void (async () => {
      settle({ ...INERT, status: 'checking' });
      try {
        const token = await getConvexAuthToken(getToken);
        const summary = await getPendingShelfPurchases(token);
        if (cancelled) return;
        if (!summary) throw new Error('Purchase service unavailable');
        if (summary.blocked) { settle({ ...INERT, status: 'verify_email' }); return; }
        if (summary.pending <= 0) { settle({ ...INERT, status: 'none' }); return; }
        settle({ ...INERT, status: 'adding' });
        const result = await claimShelfPurchases(token);
        if (!result) throw new Error('Purchase service unavailable');
        if (result.blocked) { settle({ ...INERT, status: 'verify_email' }); return; }
        settle({ status: result.claimed > 0 ? 'added' : 'none', addedCount: result.claimed, products: result.products ?? [] });
      } catch { settle({ ...INERT, status: 'unavailable' }); }
    })();
    // StrictMode can cancel and repeat the effect. The server mutation is
    // idempotent; an abandoned effect must never suppress the next attempt.
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, getToken, userId, attempt]);
  return { ...state, retry };
}
